import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { OrdersResponse, OrderSummary } from '@shared/api-responses';
import { ORDER_STATUS_LABELS, type OrderStatus, type SupplyProgram } from '@shared/types';
import { apiFetch } from '../hooks/useApi';
import ProgramBadge from '../components/ProgramBadge';
import ProgramFilter from '../components/ProgramFilter';

const STATUS_COLOR: Record<string, string> = {
  draft: 'text-zinc-400',
  submitted: 'text-ems-amber',
  approved: 'text-dcvfd-accent', // legacy — approval gate removed
  placed: 'text-dcvfd-accent',
  received: 'text-ems-green',
  cancelled: 'text-ems-red',
};

export default function Orders() {
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState<SupplyProgram | null>(null);

  useEffect(() => {
    setLoading(true);
    // Filter server-side so the count and the list can't disagree.
    apiFetch<OrdersResponse>(program ? `/orders?program=${program}` : '/orders')
      .then((r) => setOrders(r.orders))
      .finally(() => setLoading(false));
  }, [program]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-white">Orders</h1>
        <ProgramFilter value={program} onChange={setProgram} />
      </div>
      {loading ? (
        <p className="text-zinc-400">Loading…</p>
      ) : orders.length === 0 ? (
        <p className="text-zinc-500">
          {program ? 'No orders for this program yet.' : 'No orders yet. Each submitted request becomes its own order.'}
        </p>
      ) : (
        <div className="space-y-2">
          {orders.map((o) => (
            <Link
              key={o.id}
              to={`/orders/${o.id}`}
              className="glass rounded-lg px-4 py-3 flex items-center justify-between text-white hover:border-dcvfd-accent"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium capitalize truncate">
                    {o.channel === 'vendor' ? (o.vendor_name ?? 'Vendor') : 'Grocery'} order #{o.id}
                  </span>
                  {/* Always shown, even when filtered — a mixed list is otherwise ambiguous. */}
                  <ProgramBadge program={o.program} />
                </div>
                <div className="text-xs text-zinc-400">{o.item_count} items</div>
              </div>
              <span className={`text-sm font-semibold ${STATUS_COLOR[o.status] ?? 'text-white'}`}>
                {ORDER_STATUS_LABELS[o.status as OrderStatus] ?? o.status}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
